import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, Home, Heart, Target, Users, Lightbulb, BookOpen, Award, ChevronRight, Sparkles, Star, Zap, Globe, Menu, X } from 'lucide-react';
import EdpiesLogo from '@/components/EdpiesLogo';

const fadeInUp = { hidden: { opacity: 0, y: 60 }, visible: { opacity: 1, y: 0 } };
const fadeInLeft = { hidden: { opacity: 0, x: -60 }, visible: { opacity: 1, x: 0 } };
const fadeInRight = { hidden: { opacity: 0, x: 60 }, visible: { opacity: 1, x: 0 } };
const scaleIn = { hidden: { opacity: 0, scale: 0.8 }, visible: { opacity: 1, scale: 1 } };
const staggerContainer = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.15, delayChildren: 0.1 } } };

const AboutUs = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { scrollYProgress } = useScroll();
  const y1 = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, -200]);
  const opacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);

  const navItems = [
    { label: 'About Us', path: '/about' },
    { label: 'Resources', path: '/resources' },
    { label: 'Contact', path: '/contact' },
    { label: 'FAQ', path: '/faq' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 overflow-hidden">
      {/* Navigation */}
      <motion.nav 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
        className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800/50"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <motion.div 
              className="flex items-center gap-2 cursor-pointer" 
              onClick={() => navigate('/')}
              whileHover={{ scale: 1.05 }}
            >
              <EdpiesLogo size="sm" />
              <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-purple-400 to-indigo-400 bg-clip-text text-transparent">Edpies</span>
            </motion.div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-4">
              {navItems.map((item) => (
                <Button 
                  key={item.path}
                  variant="ghost" 
                  onClick={() => navigate(item.path)}
                  className={`text-slate-300 hover:text-white hover:bg-slate-800 ${item.path === '/about' ? 'text-purple-400' : ''}`}
                >
                  {item.label}
                </Button>
              ))}
              <Button 
                variant="ghost" 
                onClick={() => navigate('/auth')}
                className="text-slate-300 hover:text-white hover:bg-slate-800"
              >
                Sign In
              </Button>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button onClick={() => navigate('/auth')} className="bg-gradient-to-r from-purple-600 to-indigo-600">
                  Get Started
                </Button>
              </motion.div>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center gap-2">
              <Button 
                size="sm"
                onClick={() => navigate('/auth')}
                className="bg-gradient-to-r from-purple-600 to-indigo-600 text-xs px-3"
              >
                Sign In
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-slate-300 hover:text-white p-2"
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="md:hidden border-t border-slate-800/50 py-3"
            >
              <div className="flex flex-wrap gap-2">
                {navItems.map((item) => (
                  <Button 
                    key={item.path}
                    variant="ghost" 
                    size="sm"
                    onClick={() => {
                      navigate(item.path);
                      setMobileMenuOpen(false);
                    }}
                    className={`text-slate-300 hover:text-white ${item.path === '/about' ? 'text-purple-400' : ''}`}
                  >
                    {item.label}
                  </Button>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </motion.nav>

      {/* Hero with Parallax */}
      <section className="pt-32 pb-20 px-4 relative">
        {/* Parallax Background Elements */}
        <motion.div style={{ y: y1 }} className="absolute top-20 left-10 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl" />
        <motion.div style={{ y: y2 }} className="absolute top-40 right-20 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl" />
        
        <div className="max-w-4xl mx-auto text-center relative">
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", duration: 1, delay: 0.2 }}
            className="inline-flex items-center justify-center w-24 h-24 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 shadow-2xl shadow-purple-500/30 mb-8"
          >
            <motion.div
              animate={{ rotate: [0, 360] }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            >
              <Sparkles className="h-12 w-12 text-white" />
            </motion.div>
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 30 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-5xl md:text-7xl font-bold text-white mb-6" 
            style={{ fontFamily: 'Space Grotesk' }}
          >
            About <motion.span 
              className="bg-gradient-to-r from-purple-400 via-indigo-400 to-cyan-400 bg-clip-text text-transparent inline-block"
              animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
              transition={{ duration: 5, repeat: Infinity }}
              style={{ backgroundSize: "200% 200%" }}
            >
              Edpies
            </motion.span>
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            transition={{ delay: 0.5 }} 
            className="text-xl text-slate-400 max-w-2xl mx-auto"
          >
            Building the future of education, one learner at a time.
          </motion.p>

          {/* Scroll Indicator */}
          <motion.div 
            style={{ opacity }}
            className="absolute bottom-0 left-1/2 -translate-x-1/2"
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <div className="w-6 h-10 border-2 border-slate-600 rounded-full flex justify-center pt-2">
              <motion.div 
                className="w-1.5 h-1.5 bg-purple-400 rounded-full"
                animate={{ y: [0, 12, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Mission with Scroll Animation */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={scaleIn}
            transition={{ duration: 0.8 }}
          >
            <Card className="bg-gradient-to-br from-purple-900/40 to-indigo-900/40 border-purple-500/20 overflow-hidden relative">
              <motion.div 
                className="absolute top-0 right-0 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl"
                animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
                transition={{ duration: 4, repeat: Infinity }}
              />
              <CardContent className="p-8 md:p-12 relative">
                <motion.div 
                  className="flex items-center gap-4 mb-6"
                  initial={{ x: -50, opacity: 0 }}
                  whileInView={{ x: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <motion.div 
                    className="bg-purple-500/20 p-4 rounded-2xl"
                    whileHover={{ rotate: [0, -10, 10, 0], scale: 1.1 }}
                    transition={{ duration: 0.5 }}
                  >
                    <Target className="h-8 w-8 text-purple-400" />
                  </motion.div>
                  <h2 className="text-3xl font-bold text-white">Our Mission</h2>
                </motion.div>
                <motion.p 
                  className="text-lg text-slate-300 leading-relaxed"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  To transform education by creating an intelligent, adaptive learning platform that understands and nurtures every student's unique potential. We believe that with the right support, <span className="text-purple-400 font-semibold">every learner can achieve excellence</span>.
                </motion.p>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Values with Staggered Scroll Animations */}
      <section className="py-20 px-4 relative">
        <motion.div 
          className="absolute inset-0 bg-gradient-to-b from-transparent via-slate-900/50 to-transparent"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        />
        
        <div className="max-w-5xl mx-auto relative">
          <motion.h2 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="text-4xl font-bold text-white text-center mb-16"
          >
            Our <span className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">Values</span>
          </motion.h2>
          
          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            className="grid md:grid-cols-3 gap-8"
          >
            {[
              { icon: Heart, color: 'rose', title: 'Student-Centred', desc: 'Every feature, every decision puts the learner first. Education should adapt to students, not the other way around.', delay: 0 },
              { icon: Lightbulb, color: 'amber', title: 'Innovation', desc: 'Using cutting-edge AI to create personalised learning experiences that were impossible just years ago.', delay: 0.1 },
              { icon: Users, color: 'cyan', title: 'Inclusivity', desc: 'Education that adapts to every learning style, pace, and background. No student left behind.', delay: 0.2 }
            ].map((value, idx) => (
              <motion.div 
                key={idx} 
                variants={fadeInUp}
                whileHover={{ y: -10, transition: { duration: 0.3 } }}
              >
                <Card className="bg-slate-800/50 border-slate-700/50 hover:border-slate-600 transition-all h-full group hover:shadow-xl hover:shadow-purple-500/5">
                  <CardContent className="p-8 text-center">
                    <motion.div 
                      className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-${value.color}-500/10 mb-6 group-hover:bg-${value.color}-500/20 transition-colors`}
                      whileHover={{ rotate: 360, scale: 1.1 }}
                      transition={{ duration: 0.6 }}
                    >
                      <value.icon className={`h-8 w-8 text-${value.color}-400`} />
                    </motion.div>
                    <h3 className="text-xl font-bold text-white mb-3">{value.title}</h3>
                    <p className="text-slate-400">{value.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Story with Timeline Animation */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-white mb-4">Our <span className="bg-gradient-to-r from-purple-400 to-indigo-400 bg-clip-text text-transparent">Story</span></h2>
          </motion.div>
          
          <div className="space-y-8">
            {[
              { icon: Lightbulb, text: 'Edpies began with a simple observation: traditional education often treats students as a homogeneous group, delivering the same content at the same pace to everyone.' },
              { icon: Zap, text: 'We knew there had to be a better way. By combining cutting-edge AI technology with proven educational methodologies, we created a platform that truly understands each learner.' },
              { icon: Globe, text: 'Edpies helps students across the UK prepare for 7+, 11+, GCSEs, A-Levels, and beyond. Our adaptive learning system ensures that no student is left behind and no potential goes unrealised.' },
              { icon: Star, text: 'Our vision is to make world-class, personalised education accessible to every student, regardless of their background or circumstances. The future of learning is here.' }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={idx % 2 === 0 ? fadeInLeft : fadeInRight}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                className="flex gap-6 items-start"
              >
                <motion.div 
                  className="bg-purple-500/10 p-3 rounded-xl flex-shrink-0"
                  whileHover={{ scale: 1.2, rotate: 10 }}
                >
                  <item.icon className="h-6 w-6 text-purple-400" />
                </motion.div>
                <p className="text-lg text-slate-300 leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={scaleIn}
            transition={{ duration: 0.8 }}
          >
            <motion.h2 
              className="text-4xl font-bold text-white mb-6"
              animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
            >
              Ready to Start Your <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">Journey</span>?
            </motion.h2>
            <p className="text-xl text-slate-400 mb-10">Start your learning journey and achieve your full potential with Edpies.</p>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button 
                onClick={() => navigate('/auth')} 
                size="lg" 
                className="h-16 px-12 text-xl bg-gradient-to-r from-purple-600 via-indigo-600 to-cyan-600 hover:from-purple-700 hover:via-indigo-700 hover:to-cyan-700 shadow-xl shadow-purple-500/20"
              >
                Get Started Free <ChevronRight className="ml-2 h-6 w-6" />
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-slate-800">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <p className="text-slate-500 text-sm">© 2025 Edpies</p>
          <div className="flex gap-4">
            <Button variant="ghost" size="sm" className="text-slate-400" onClick={() => navigate('/')}>Home</Button>
            <Button variant="ghost" size="sm" className="text-slate-400" onClick={() => navigate('/faq')}>FAQ</Button>
            <Button variant="ghost" size="sm" className="text-slate-400" onClick={() => navigate('/contact')}>Contact</Button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutUs;
